import { PageIntro } from '../../components/dugout/PageIntro'
import React from 'react'
import { Link } from 'react-router-dom'
import { DugoutNav } from '@/components/dugout/DugoutNav'
import { DugoutFooter } from '@/components/dugout/DugoutFooter'
import { Card, CardContent } from '@/components/ui/card'
import {
  ArrowRight,
  Hexagon,
  Sparkles,
  Leaf,
  Users,
  TrendingUp,
  Heart,
  Quote,
  CheckCircle2,
  MapPin
} from 'lucide-react'
import '../../theme/dugout-redesign.css'

// Base URL for assets (handles /dugout/ prefix)
const baseUrl = import.meta.env.BASE_URL || '/'

// Real case studies
const caseStudies = [
  {
    id: 'sjhunny',
    title: 'SJ Hunny',
    tagline: 'From Hive to Home',
    client: 'Andrew',
    location: 'East Foothills, San Jose',
    category: 'Brand & Community Education',
    color: '#F59E0B',
    logo: '\u{1F41D}',
    heroImage: `${baseUrl}images/heritage/eastside-golden-hills.jpg`,
    story: `It started with a conversation about bees. Andrew, a local beekeeper tending hives in the East Foothills—what he calls the "Golden Hive of 100 Acre Hills"—wanted to do more than sell honey. He wanted to educate the community about why bees matter and how supporting local beekeepers creates a healthier ecosystem for everyone.

Together, we built SJ Hunny: not just a honey brand, but a platform for bee education and local food advocacy. The name celebrates San Jose's agricultural roots while the brand tells the story of urban beekeeping in Silicon Valley's backyard.`,
    challenge: 'Transform a local beekeeping operation into an educational brand that connects community members with the importance of pollinators while creating a sustainable business model.',
    approach: [
      'Deep discovery sessions to understand Andrew\'s passion for bee education',
      'Brand identity that honors both the craft and the East Foothills terroir',
      'Educational content strategy—workshops, school visits, farmers market presence',
      'Packaging design that tells the story of each harvest location'
    ],
    impact: [
      { icon: Hexagon, metric: 'Brand', label: 'Identity System' },
      { icon: Users, metric: 'Education', label: 'Content Strategy' },
      { icon: Leaf, metric: 'Local', label: 'Terroir Story' }
    ],
    testimonial: {
      quote: "Jay helped me see that I wasn't just selling honey—I was sharing a story about our land, our bees, and our community. SJ Hunny is now how I introduce people to the magic happening in our foothills.",
      author: "Andrew",
      title: "Founder & Beekeeper"
    },
    lessons: 'The best local brands don\'t just sell products—they educate and connect. When you buy SJ Hunny, you\'re supporting pollinators, learning about your ecosystem, and tasting the literal flavor of the East Foothills.'
  },
  {
    id: 'pierce-design',
    title: 'Pierce Design',
    tagline: 'Craft Meets Client',
    client: 'Pierce Construction',
    location: 'San Jose, CA',
    category: 'Brand & Digital Transformation',
    color: '#22C55E',
    logo: '\u{1F528}',
    heroImage: `${baseUrl}images/portfolio/pierce-design-hero.png`,
    story: `Pierce came to us as a skilled sub-contractor with years of craftsmanship under his belt, but his business looked like every other contractor's: word-of-mouth referrals, a basic website, and no system for handling leads. He knew his work was excellent—but potential clients couldn't see that quality before hiring him.

We started with brand creation: a visual identity that reflected his precision and attention to detail. Then we built a digital onboarding system powered by AI that qualifies leads, schedules consultations, and showcases his portfolio—all while he's on the job site.`,
    challenge: 'Elevate a sub-contractor from commodity service provider to premium design-build partner, while automating client acquisition so he could focus on craft.',
    approach: [
      'Brand audit and competitive positioning—what makes Pierce different?',
      'Visual identity system: logo, colors, photography direction',
      'AI-powered lead qualification chatbot for 24/7 client intake',
      'Portfolio showcase emphasizing design sensibility, not just construction',
      'Unlocking his creative side through design-build project positioning'
    ],
    impact: [
      { icon: TrendingUp, metric: 'AI', label: 'Lead Qualification' },
      { icon: Sparkles, metric: 'Premium', label: 'Positioning' },
      { icon: Heart, metric: 'Design-Build', label: 'Brand Identity' }
    ],
    testimonial: {
      quote: "I used to spend half my evenings answering the same questions from tire-kickers. Now the AI handles intake, I only talk to serious clients, and my brand finally shows the quality of my work. Jay didn't just build me a website—he gave me my evenings back.",
      author: "Pierce",
      title: "Owner, Pierce Design"
    },
    lessons: 'Contractors and tradespeople often undervalue their creative instincts. By positioning Pierce as a design-build partner (not just a sub), we attracted clients who appreciate craft—and pay accordingly.'
  },
  {
    id: 'kaeru',
    title: 'Kaeru',
    tagline: 'Ritual Over Product',
    client: 'Scott',
    location: 'Bay Area',
    category: 'Brand Strategy & Positioning',
    color: '#0EA5E9',
    logo: '\u{1F438}',
    heroImage: `${baseUrl}images/labs/kaeru-shrine.jpg`,
    story: `Kaeru came to us with CBD products—balms, lotions, tinctures. Good products, but they were competing in a crowded market where everyone was selling jars and milligrams. The real opportunity wasn't in the product itself.

Through our discovery process, we identified what made Kaeru different: the founders cared deeply about the ritual of self-care, the ceremonial aspects of slowing down and applying these products with intention. They weren't selling CBD—they were selling a practice.

We repositioned Kaeru as a health and wellness brand focused on ritual and ceremony. The products became tools for a practice, not commodities on a shelf.`,
    challenge: 'Differentiate a CBD brand in an oversaturated market by finding the authentic story that competitors couldn\'t copy.',
    approach: [
      'Deep discovery to uncover founder values and customer motivations',
      'Market analysis revealing the "ritual wellness" whitespace',
      'Brand repositioning from product-centric to practice-centric',
      'Messaging framework emphasizing ceremony, intention, and self-care rituals',
      'Visual identity that evokes calm, intention, and natural luxury'
    ],
    impact: [
      { icon: Heart, metric: 'Ritual', label: 'Brand Strategy' },
      { icon: TrendingUp, metric: 'Premium', label: 'Positioning' },
      { icon: Users, metric: 'Wellness', label: 'Market Focus' }
    ],
    testimonial: {
      quote: "We were stuck competing on price and potency like everyone else. Jay helped us realize we weren't in the CBD business—we were in the ritual business. That shift changed everything about how we talk to customers and how they experience our brand.",
      author: "Scott",
      title: "Founder, Kaeru"
    },
    lessons: 'The best brand strategy often means zooming out from what you sell to understand what you\'re really offering. Kaeru\'s products didn\'t change—but their story did, and that story commands premium positioning.'
  }
]

const ClientPortfolio = () => {
  return (
    <div className="dugout-redesign min-h-screen bg-[var(--dug-bg-deep)] text-[var(--dug-text-primary)]">
      <DugoutNav />

      <main>
        <PageIntro
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Work', href: '/portfolio' }, { label: 'Client stories' }]}
          kicker="PORTFOLIO · REAL STORIES"
          title={<>Work in the <em>Wild</em></>}
          lead="Real partnerships with real people building real businesses. These are the stories of how we've helped local entrepreneurs find their voice, reach their customers, and grow sustainably."
        />

        {/* Case Studies */}
        {caseStudies.map((study, idx) => {
          const isEven = idx % 2 === 0

          // Helper to convert hex to rgba
          const hexToRgba = (hex: string, alpha: number) => {
            const r = parseInt(hex.slice(1, 3), 16)
            const g = parseInt(hex.slice(3, 5), 16)
            const b = parseInt(hex.slice(5, 7), 16)
            return `rgba(${r}, ${g}, ${b}, ${alpha})`
          }

          // Different background treatments for each case study - dark theme
          const bgStyles = [
            // SJ Hunny - Honeycomb pattern with warm amber tint on dark
            {
              background: `linear-gradient(135deg, ${hexToRgba(study.color, 0.08)} 0%, transparent 50%, ${hexToRgba(study.color, 0.04)} 100%)`,
              pattern: `url("data:image/svg+xml,%3Csvg width='56' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M28 66L0 50L0 16L28 0L56 16L56 50L28 66L28 100' fill='none' stroke='${encodeURIComponent(study.color)}' stroke-opacity='0.15' stroke-width='1'/%3E%3Cpath d='M28 0L28 34L0 50L0 84L28 100L56 84L56 50L28 34' fill='none' stroke='${encodeURIComponent(study.color)}' stroke-opacity='0.15' stroke-width='1'/%3E%3C/svg%3E")`,
              sectionBg: 'bg-[var(--dug-bg-surface)]',
            },
            // Pierce Design - Grid/blueprint pattern with green tint on dark
            {
              background: `linear-gradient(180deg, var(--dug-bg-deep) 0%, ${hexToRgba(study.color, 0.06)} 50%, var(--dug-bg-deep) 100%)`,
              pattern: `url("data:image/svg+xml,%3Csvg width='40' height='40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h40v40H0z' fill='none'/%3E%3Cpath d='M0 20h40M20 0v40' stroke='${encodeURIComponent(study.color)}' stroke-opacity='0.15' stroke-width='1'/%3E%3Cpath d='M0 0h40v40H0z' fill='none' stroke='${encodeURIComponent(study.color)}' stroke-opacity='0.1'/%3E%3C/svg%3E")`,
              sectionBg: 'bg-[var(--dug-bg-elevated)]',
            },
            // Kaeru - Organic flowing pattern with sky blue tint on dark
            {
              background: `radial-gradient(ellipse at 30% 20%, ${hexToRgba(study.color, 0.1)} 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, ${hexToRgba(study.color, 0.06)} 0%, transparent 50%)`,
              pattern: `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='30' cy='30' r='20' fill='none' stroke='${encodeURIComponent(study.color)}' stroke-opacity='0.12' stroke-width='1'/%3E%3Ccircle cx='30' cy='30' r='10' fill='none' stroke='${encodeURIComponent(study.color)}' stroke-opacity='0.08' stroke-width='1'/%3E%3C/svg%3E")`,
              sectionBg: 'bg-[var(--dug-bg-surface)]',
            },
          ]

          const currentBg = bgStyles[idx]

          return (
            <section
              key={study.id}
              className={`relative py-20 md:py-28 overflow-hidden ${currentBg.sectionBg}`}
              style={{ background: currentBg.background }}
            >
              {/* Pattern overlay */}
              <div
                className="absolute inset-0"
                style={{ backgroundImage: currentBg.pattern }}
              />

              {/* Top decorative border */}
              {idx > 0 && (
                <div
                  className="absolute top-0 left-0 right-0 h-px"
                  style={{
                    background: `linear-gradient(90deg, transparent 0%, ${study.color}50 50%, transparent 100%)`
                  }}
                />
              )}

              {/* Grain texture */}
              <div className="absolute inset-0 dug-bg-grain opacity-50" />

              {/* Accent glow */}
              <div
                className="absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl opacity-20"
                style={{ backgroundColor: study.color }}
              />
              <div
                className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl opacity-10"
                style={{ backgroundColor: study.color }}
              />

              <div className="dug-container relative z-10">
                {/* Client logo */}
                <div className="flex justify-center mb-10">
                  <span className="text-6xl">{study.logo}</span>
                </div>

                <article className="relative">
                  <div className={`grid gap-12 lg:grid-cols-2 items-start ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                    {/* Visual */}
                    <div className={!isEven ? 'lg:order-2' : ''}>
                      <div
                        className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl"
                        style={{
                          background: `linear-gradient(135deg, ${study.color}30 0%, ${study.color}10 100%)`,
                          boxShadow: `0 25px 50px -12px ${study.color}30`
                        }}
                      >
                        {/* Background image */}
                        <div
                          className="absolute inset-0 opacity-40 bg-cover bg-center bg-no-repeat"
                          style={{ backgroundImage: `url(${study.heroImage})` }}
                        />

                        {/* Gradient overlay */}
                        <div
                          className="absolute inset-0"
                          style={{
                            background: `linear-gradient(135deg, ${study.color}40 0%, transparent 60%, ${study.color}20 100%)`
                          }}
                        />

                        {/* Content overlay */}
                        <div className="relative z-10 h-full flex flex-col justify-between p-8">
                          <div>
                            <span
                              className="inline-block px-3 py-1 rounded-full text-xs font-medium text-white mb-4 shadow-lg"
                              style={{ backgroundColor: study.color }}
                            >
                              {study.category}
                            </span>
                            <h2 className="mb-2 drop-shadow-lg text-white ds-type-section">
                              {study.title}
                            </h2>
                            <p className="text-white/80 italic ds-type-body">
                              {study.tagline}
                            </p>
                          </div>

                          <div className="flex items-center gap-2 text-sm text-white/70">
                            <MapPin className="w-4 h-4" />
                            {study.location}
                          </div>
                        </div>
                      </div>

                      {/* Impact metrics */}
                      <div
                        className="grid grid-cols-3 gap-4 mt-6 p-5 rounded-xl border backdrop-blur-sm bg-[var(--dug-bg-elevated)]/80"
                        style={{ borderColor: `${study.color}40` }}
                      >
                        {study.impact.map((item, i) => {
                          const Icon = item.icon
                          return (
                            <div key={i} className="text-center">
                              <Icon className="w-6 h-6 mx-auto mb-2" style={{ color: study.color }} />
                              <div className="text-2xl font-bold" style={{ color: study.color }}>
                                {item.metric}
                              </div>
                              <div className="text-xs text-[var(--dug-text-muted)] uppercase tracking-wide">
                                {item.label}
                              </div>
                            </div>
                          )
                        })}
                      </div>

                      {/* Lesson */}
                      <div
                        className="mt-6 p-5 rounded-xl border"
                        style={{
                          backgroundColor: `${study.color}15`,
                          borderColor: `${study.color}30`
                        }}
                      >
                        <h4
                          className="text-sm font-bold mb-2 uppercase tracking-wide"
                          style={{ color: study.color }}
                        >
                          The Lesson
                        </h4>
                        <p className="text-[var(--dug-text-primary)] leading-relaxed ds-type-body">
                          {study.lessons}
                        </p>
                      </div>
                    </div>

                    {/* Content */}
                    <div className={!isEven ? 'lg:order-1' : ''}>
                      {/* Story */}
                      <div className="prose prose-lg max-w-none mb-8">
                        {study.story.split('\n\n').map((paragraph, i) => {
                          if (i === 0) {
                            const firstSentenceEnd = paragraph.indexOf('. ')
                            if (firstSentenceEnd !== -1) {
                              const firstSentence = paragraph.slice(0, firstSentenceEnd + 1)
                              const rest = paragraph.slice(firstSentenceEnd + 1)
                              return (
                                <p key={i} className="text-[var(--dug-text-secondary)] leading-relaxed mb-4 ds-type-body">
                                  <span className="text-[var(--dug-text-primary)] font-medium text-lg">{firstSentence}</span>
                                  {rest}
                                </p>
                              )
                            }
                          }
                          return (
                            <p key={i} className="text-[var(--dug-text-secondary)] leading-relaxed mb-4 ds-type-body">
                              {paragraph}
                            </p>
                          )
                        })}
                      </div>

                      {/* Challenge */}
                      <div
                        className="mb-8 p-5 rounded-xl border bg-[var(--dug-bg-elevated)]/50"
                        style={{ borderColor: `${study.color}30` }}
                      >
                        <h4
                          className="text-sm font-semibold mb-2 uppercase tracking-wide"
                          style={{ color: study.color }}
                        >
                          The Challenge
                        </h4>
                        <p className="text-[var(--dug-text-secondary)] ds-type-body">{study.challenge}</p>
                      </div>

                      {/* Approach */}
                      <div className="mb-8">
                        <h4 className="text-sm font-semibold text-[var(--dug-text-primary)] mb-4 uppercase tracking-wide">
                          Our Approach
                        </h4>
                        <ul className="space-y-3">
                          {study.approach.map((item, i) => (
                            <li key={i} className="flex items-start gap-3 text-[var(--dug-text-secondary)]">
                              <CheckCircle2
                                className="w-5 h-5 flex-shrink-0 mt-0.5"
                                style={{ color: study.color }}
                              />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Testimonial */}
                      <Card
                        className="border-l-4 shadow-lg bg-[var(--dug-bg-elevated)] border-[var(--dug-border)]"
                        style={{ borderLeftColor: study.color }}
                      >
                        <CardContent className="p-6">
                          <Quote className="w-8 h-8 mb-3" style={{ color: `${study.color}60` }} />
                          <p className="text-[var(--dug-text-secondary)] italic mb-4 leading-relaxed ds-type-body">
                            "{study.testimonial.quote}"
                          </p>
                          <div className="flex items-center gap-3">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                              style={{ backgroundColor: study.color }}
                            >
                              {study.testimonial.author.charAt(0)}
                            </div>
                            <div>
                              <div className="font-semibold text-[var(--dug-text-primary)]">
                                {study.testimonial.author}
                              </div>
                              <div className="text-sm text-[var(--dug-text-muted)]">
                                {study.testimonial.title}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                    </div>
                  </div>
                </article>
              </div>
            </section>
          )
        })}

        {/* Philosophy Section */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-[var(--dug-bg-elevated)]">
          {/* Grain texture */}
          <div className="absolute inset-0 dug-bg-grain" />

          <div className="dug-container relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="dug-overline mb-6 flex items-center justify-center gap-3">
                <span className="w-8 h-px bg-[var(--dug-coral)]" />
                Our Approach
                <span className="w-8 h-px bg-[var(--dug-coral)]" />
              </div>
              <h2 className="dug-display-sm mb-6 ds-type-section">
                Every Project Tells a Story
              </h2>
              <p className="dug-body-lg mb-12 max-w-2xl mx-auto ds-type-body">
                We don't do cookie-cutter solutions. Each partnership starts with deep listening—understanding
                not just what you sell, but why you started, who you serve, and what success really looks like for you.
              </p>
              <div className="grid md:grid-cols-3 gap-8 text-left">
                <div className="dug-card p-6 group hover:border-[var(--dug-coral)]/50 transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Heart className="w-6 h-6 text-amber-400" />
                  </div>
                  <h3 className="text-[var(--dug-text-primary)] mb-2 ds-type-card">Story First</h3>
                  <p className="text-[var(--dug-text-secondary)] leading-relaxed ds-type-body">
                    We find the authentic narrative that competitors can't copy.
                  </p>
                </div>
                <div className="dug-card p-6 group hover:border-[var(--dug-coral)]/50 transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Sparkles className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="text-[var(--dug-text-primary)] mb-2 ds-type-card">Systems Thinking</h3>
                  <p className="text-[var(--dug-text-secondary)] leading-relaxed ds-type-body">
                    We build tools and workflows that work while you sleep.
                  </p>
                </div>
                <div className="dug-card p-6 group hover:border-[var(--dug-coral)]/50 transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Users className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-[var(--dug-text-primary)] mb-2 ds-type-card">Local Impact</h3>
                  <p className="text-[var(--dug-text-secondary)] leading-relaxed ds-type-body">
                    10% of every project reinvests into the Commons community.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-24 md:py-32 overflow-hidden bg-[var(--dug-bg-deep)]">
          {/* Grid pattern */}
          <div className="absolute inset-0 dug-grid-bg opacity-20" />
          {/* Grain texture */}
          <div className="absolute inset-0 dug-bg-grain" />

          <div className="dug-container relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <div className="dug-status mb-6">
                Taking on new projects
              </div>
              <h2 className="dug-display mb-6 ds-type-section">
                What's Your <em>Story</em>?
              </h2>
              <p className="dug-body-lg mb-10 max-w-2xl mx-auto ds-type-body">
                Whether you're a beekeeper, a contractor, a wellness brand, or something we've never
                worked with before—we'd love to hear what you're building.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link to="/#contact" className="dug-btn dug-btn-primary">
                  Talk to Us
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/" className="dug-btn dug-btn-secondary">
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <DugoutFooter />
    </div>
  )
}

export default ClientPortfolio
